const getApiBaseUrl = (): string => {
  const configured = import.meta.env.VITE_BACKEND_URL;
  if (configured) {
    return `${configured.replace(/\/+$/, '').replace(/\/api$/, '')}/api`;
  }
  // In development, default to local backend on port 3001
  if (import.meta.env.DEV) {
    return 'http://localhost:3001/api';
  }
  return '/api';
};

const API_BASE_URL = getApiBaseUrl();

export interface LeadData {
  fullName: string
  email: string
  phone: string
  province: string
  city: string
  companyName: string
  companyUrl?: string
  businessType: string
  paymentVolume: string
  paymentSolution: string
  additionalInfo: string
  source?: string
}

export interface ApiResponse<T = any> {
  success: boolean
  data?: T
  message: string
  errors?: Record<string, string[]>
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public errors?: Record<string, string[]>
  ) {
    super(message)
    this.name = 'ApiError'
    Object.setPrototypeOf(this, ApiError.prototype)
  }
}

// Generic API request function
async function apiRequest<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  try {
    const url = `${API_BASE_URL}${endpoint}`
    
    const defaultOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'omit',
      referrerPolicy: 'same-origin',
    }

    const response = await fetch(url, {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...options.headers,
      },
    })

    // Check if response has content to parse
    const contentType = response.headers.get('content-type')
    let result: ApiResponse<T>
    
    if (contentType && contentType.includes('application/json')) {
      const text = await response.text()
      if (text.trim()) {
        try {
          result = JSON.parse(text)
        } catch (e) {
          throw new ApiError('Invalid JSON response from server')
        }
      } else {
        // Empty response, create a default response
        result = {
          success: response.ok,
          message: response.ok ? 'Request completed successfully' : 'Request failed'
        }
      }
    } else {
      // Non-JSON response
      result = {
        success: response.ok,
        message: response.ok ? 'Request completed successfully' : 'Request failed'
      }
    }

    if (!response.ok) {
      throw new ApiError(
        result.message || 'API request failed',
        response.status,
        result.errors
      )
    }

    return result
  } catch (error) {
    // Re-throw API errors immediately
    if (error instanceof ApiError || (error as any)?.name === 'ApiError') {
      throw error
    }

    // Handle network errors
    if (error instanceof TypeError || (error instanceof Error && error.message.toLowerCase().includes('fetch'))) {
      throw new ApiError(
        'Unable to connect to the server. Please check your internet connection and try again.'
      )
    }
    
    // Handle other errors
    throw new ApiError(
      error instanceof Error ? error.message : 'An unexpected error occurred'
    )
  }
}

// Lead API functions
export const leadApi = {
  /**
   * Submit a new lead application
   */
  async submitLead(leadData: Omit<LeadData, 'source'>): Promise<ApiResponse> {
    const data: LeadData = {
      ...leadData,
      source: 'website'
    }
    
    return apiRequest('/leads', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },
}

export default { leadApi }
